import React, { useState, useEffect } from "react";
import CreateRoomClassModal from "./createRoomClass";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  deleteRoomClass,
  getRoomClassByResortId,
} from "../../../../../../../apis/property";
import EditRoomClass from "./editRoomClass";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SearchIcon,
  ViewGridIcon,
  ViewListIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";

const RoomClass = () => {
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [roomClass, setRoomClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list");

  const fetchRoomClass = async () => {
    try {
      const response = await getRoomClassByResortId(id);
      setRoomClass(response.data);
      setLoading(false);
    } catch (error) {
      console.log("Error fetching room class");
      setLoading(false);
    }
  };

  // Fetch the initial list when the property page mounts.
  useEffect(() => {
    fetchRoomClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    await fetchRoomClass();
  };

  const openEditModal = (roomId) => {
    setSelectedRoomId(roomId);
    setIsEditModalOpen(true);
  };

  const closeEditModal = async () => {
    setIsEditModalOpen(false);
    await fetchRoomClass();
    setSelectedRoomId(null);
  };

  const handleDelete = async (resort_class_id) => {
    if (window.confirm("Are you sure you want to delete this room class?")) {
      try {
        await deleteRoomClass(resort_class_id);
        toast.success("Room Class deleted successfully");
        await fetchRoomClass();
      } catch (error) {
        toast.error("Failed to delete Room Class");
      }
    }
  };

  // Filter room classes based on search
  const filteredRoomClass = roomClass?.filter(
    (room) =>
      room.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      room.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      room.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const roomClassColors = [
    "from-blue-400 to-blue-600",
    "from-purple-400 to-purple-600",
    "from-pink-400 to-pink-600",
    "from-green-400 to-green-600",
    "from-orange-400 to-orange-600",
    "from-indigo-400 to-indigo-600",
  ];

  return (
    <div className="room-class-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Room Class Configuration</h1>
          <p className="page-subtitle">
            Manage room types and classifications for your property
          </p>
        </div>
        <button onClick={openModal} className="add-room-btn">
          <PlusIcon className="w-5 h-5" />
          <span>Add Room Class</span>
        </button>
      </div>

      {/* Stats Card */}
      <div className="stats-card">
        <div className="stat-item">
          <OfficeBuildingIcon className="stat-icon" />
          <div>
            <p className="stat-label">Total Room Classes</p>
            <h3 className="stat-value">{roomClass?.length || 0}</h3>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-wrapper">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search room classes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-actions">
          <div className="results-count">
            <span>
              {filteredRoomClass?.length || 0}{" "}
              {filteredRoomClass?.length === 1 ? "Class" : "Classes"}
            </span>
          </div>

          {/* View Toggle */}
          <div className="view-toggle">
            <button
              onClick={() => setViewMode("list")}
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading room classes...</p>
        </div>
      ) : viewMode === "list" ? (
        /* List View */
        <div className="table-container">
          <table className="room-table">
            <thead>
              <tr>
                <th>Room Class Name</th>
                <th>Code</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoomClass && filteredRoomClass.length > 0 ? (
                filteredRoomClass.map((room, idx) => (
                  <tr key={room.id}>
                    <td>
                      <div className="room-name-cell">
                        <div
                          className={`room-icon bg-gradient-to-br ${
                            roomClassColors[idx % roomClassColors.length]
                          }`}
                        >
                          <OfficeBuildingIcon className="w-5 h-5" />
                        </div>
                        <span className="room-name">{room.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="room-code">{room.code}</span>
                    </td>
                    <td>
                      <span className="room-description">
                        {room.description}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => openEditModal(room.id)}
                          className="action-btn action-btn-edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(room.id)}
                          className="action-btn action-btn-delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="empty-state">
                      <OfficeBuildingIcon className="w-16 h-16 text-gray-400" />
                      <h3>No Room Classes Found</h3>
                      <p>
                        {searchQuery
                          ? "Try adjusting your search criteria"
                          : "Get started by adding your first room class"}
                      </p>
                      {!searchQuery && (
                        <button onClick={openModal} className="empty-add-btn">
                          <PlusIcon className="w-5 h-5" />
                          <span>Add Room Class</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid-view">
          {filteredRoomClass && filteredRoomClass.length > 0 ? (
            filteredRoomClass.map((room, idx) => (
              <div key={room.id} className="room-card">
                <div
                  className={`room-card-header bg-gradient-to-br ${
                    roomClassColors[idx % roomClassColors.length]
                  }`}
                >
                  <OfficeBuildingIcon className="room-card-icon" />
                  <span className="room-card-code">{room.code}</span>
                </div>
                <div className="room-card-body">
                  <h3 className="room-card-name">{room.name}</h3>
                  <p className="room-card-description">{room.description}</p>
                  <div className="room-card-actions">
                    <button
                      onClick={() => openEditModal(room.id)}
                      className="card-action-btn card-action-btn-edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(room.id)}
                      className="card-action-btn card-action-btn-delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state grid-empty">
              <OfficeBuildingIcon className="w-16 h-16 text-gray-400" />
              <h3>No Room Classes Found</h3>
              <p>
                {searchQuery
                  ? "Try adjusting your search criteria"
                  : "Get started by adding your first room class"}
              </p>
              {!searchQuery && (
                <button onClick={openModal} className="empty-add-btn">
                  <PlusIcon className="w-5 h-5" />
                  <span>Add Room Class</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateRoomClassModal isOpen={isModalOpen} onClose={closeModal} />
      <EditRoomClass
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        selectedRoomId={selectedRoomId}
      />

      {/* Styles */}
      <style jsx>{`
        .room-class-page {
          padding: 0;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .page-subtitle {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .add-room-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(139, 92, 246, 0.3);
        }

        .add-room-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .stats-card {
          background: linear-gradient(135deg, #667eea, #764ba2);
          padding: 1.5rem;
          border-radius: 1rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 25px -5px rgba(102, 126, 234, 0.3);
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          color: white;
        }

        .stat-icon {
          width: 3rem;
          height: 3rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 0.75rem;
          backdrop-filter: blur(10px);
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 0.25rem 0;
        }

        .stat-value {
          font-size: 1.875rem;
          font-weight: 700;
          margin: 0;
        }

        .filters-section {
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .search-wrapper {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          width: 1.25rem;
          height: 1.25rem;
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 3rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #8b5cf6;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .results-count {
          padding: 0.5rem 1rem;
          background: #f3f4f6;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .view-toggle {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .toggle-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .toggle-btn.active {
          background: white;
          color: #8b5cf6;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
        }

        .spinner {
          width: 3rem;
          height: 3rem;
          border: 4px solid #e5e7eb;
          border-top-color: #8b5cf6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .table-container {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .room-table {
          width: 100%;
          border-collapse: collapse;
        }

        .room-table thead {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .room-table th {
          padding: 1rem 1.5rem;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .room-table td {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .room-table tbody tr {
          transition: background 0.2s;
        }

        .room-table tbody tr:hover {
          background: #f9fafb;
        }

        .room-name-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .room-icon {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .room-name {
          font-weight: 600;
          color: #1f2937;
        }

        .room-code {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #eff6ff;
          color: #2563eb;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .room-description {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem;
          border: none;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .action-btn-edit:hover {
          background: #dbeafe;
          transform: scale(1.1);
        }

        .action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .action-btn-delete:hover {
          background: #fee2e2;
          transform: scale(1.1);
        }

        .grid-view {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .room-card {
          background: white;
          border-radius: 1rem;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .room-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .room-card-header {
          padding: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .room-card-header::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 150px;
          height: 150px;
          background: radial-gradient(
            circle,
            rgba(255, 255, 255, 0.2) 0%,
            transparent 70%
          );
          border-radius: 50%;
          transform: translate(30%, -30%);
        }

        .room-card-icon {
          width: 3rem;
          height: 3rem;
          color: white;
          z-index: 1;
        }

        .room-card-code {
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 9999px;
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          z-index: 1;
        }

        .room-card-body {
          padding: 1.5rem;
        }

        .room-card-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.75rem 0;
        }

        .room-card-description {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0 0 1.5rem 0;
          line-height: 1.6;
        }

        .room-card-actions {
          display: flex;
          gap: 0.75rem;
        }

        .card-action-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 0.5rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .card-action-btn-edit {
          background: #eff6ff;
          color: #2563eb;
        }

        .card-action-btn-edit:hover {
          background: #dbeafe;
          transform: translateY(-1px);
        }

        .card-action-btn-delete {
          background: #fef2f2;
          color: #dc2626;
        }

        .card-action-btn-delete:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin: 1rem 0 0.5rem 0;
        }

        .empty-state p {
          color: #6b7280;
          margin: 0 0 1.5rem 0;
        }

        .empty-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          font-weight: 600;
          border-radius: 0.75rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .empty-add-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .grid-empty {
          grid-column: 1 / -1;
        }

        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .add-room-btn {
            width: 100%;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-wrapper {
            max-width: 100%;
          }

          .filter-actions {
            justify-content: space-between;
          }
        }

        @media (max-width: 768px) {
          .page-title {
            font-size: 1.5rem;
          }

          .grid-view {
            grid-template-columns: 1fr;
          }

          .room-table {
            display: block;
            overflow-x: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default RoomClass;
